import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogoMark } from "./Logo";
import { useAuth } from "../context/AuthContext";
import { LogOut, LayoutDashboard, FilePlus, Users, FileText } from "lucide-react";

const PortalLayout = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const isAdmin = user?.role === "admin";

    const items = [
        { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
        { to: "/portal/rfqs", label: "RFQs", icon: FileText },
        ...(isAdmin
            ? [{ to: "/portal/users", label: "Users", icon: Users }]
            : [{ to: "/portal/rfqs/new", label: "New RFQ", icon: FilePlus }]),
    ];

    const onLogout = () => {
        logout();
        navigate("/portal/login", { replace: true });
    };

    return (
        <div
            className="min-h-screen flex flex-col"
            style={{ background: "#f6f7f9" }}
            data-testid="portal-shell"
        >
            {/* Top bar */}
            <header className="bg-[var(--ns-ink)] text-white">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                    <Link to="/portal" className="flex items-center gap-3">
                        <LogoMark size={28} stroke="#ffffff" />
                        <div className="leading-none">
                            <div className="font-display text-sm font-semibold tracking-tight">
                                Nador Sage
                            </div>
                            <div className="font-body text-[10px] uppercase tracking-[0.22em] mt-1 opacity-75">
                                RFQ Portal
                            </div>
                        </div>
                    </Link>

                    <div className="flex items-center gap-6">
                        <div className="hidden sm:block text-right leading-tight">
                            <div className="font-body text-sm font-medium">
                                {user?.name || user?.email}
                            </div>
                            <div className="font-body text-[10px] uppercase tracking-[0.22em] opacity-75">
                                {user?.role}
                            </div>
                        </div>
                        <Link
                            to="/"
                            className="font-body text-xs uppercase tracking-[0.18em] opacity-75 hover:opacity-100 hidden md:inline"
                        >
                            Back to site
                        </Link>
                        <button
                            onClick={onLogout}
                            className="inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.18em] px-3 py-2 border border-white/20 hover:bg-white hover:text-[var(--ns-ink)] transition-colors"
                            data-testid="portal-logout"
                        >
                            <LogOut size={14} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            {/* Sub-nav */}
            <nav className="bg-white border-b border-[var(--ns-border)]">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-10 flex items-center gap-1 overflow-x-auto">
                    {items.map((it) => (
                        <NavLink
                            key={it.to}
                            to={it.to}
                            end={it.end}
                            data-testid={`portal-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `inline-flex items-center gap-2 px-4 py-4 font-body text-[13px] border-b-2 transition-colors ${
                                    isActive
                                        ? "border-[var(--ns-ink)] text-[var(--ns-ink)] font-semibold"
                                        : "border-transparent text-[var(--ns-muted)] hover:text-[var(--ns-ink)]"
                                }`
                            }
                        >
                            <it.icon size={15} strokeWidth={2} /> {it.label}
                        </NavLink>
                    ))}
                </div>
            </nav>

            <main className="flex-1">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10 lg:py-12">
                    <Outlet />
                </div>
            </main>

            <footer className="bg-white border-t border-[var(--ns-border)] py-5">
                <div className="max-w-[1440px] mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                    <div
                        className="font-body text-[11px] uppercase tracking-[0.22em]"
                        style={{ color: "var(--ns-muted)" }}
                    >
                        Aerospace Procurement & RFQ Management Portal
                    </div>
                    <div
                        className="font-body text-[11px] uppercase tracking-[0.22em]"
                        style={{ color: "var(--ns-muted)" }}
                    >
                        © {new Date().getFullYear()} Nador Sage International S.r.l.
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default PortalLayout;
